import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useWarrantProgramSuspenseQuery } from '../../../WarrantProgram.graphql';
import { Trans, useLingui } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Table } from '@startuptools/ui/Table';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';
import { toDateTime } from '@startuptools/common/common';
import { wpEditOptionsSubsLink } from '../../../../../components/Links';
import { useNavigate } from '../../../../../components/Navigate';
import { Link } from 'react-router-dom';

export const WiseStepSummaryNgWrap = ({ warrantProgramId }: { warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const {
    data: { warrantProgram },
  } = useWarrantProgramSuspenseQuery({ variables: { companyId, warrantProgramId }, fetchPolicy: 'cache-and-network' });

  return <WiseStepSummary warrantProgram={warrantProgram} />;
};

export const WiseStepSummary = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'warrantKind'
    | 'usesStepper'
    | 'illiquidityDiscount'
    | 'pricePerWarrant'
    | 'strikePrice'
    | 'warrantStartDate'
    | 'warrantEndDate'
    | 'shareSubscribeStartDate'
    | 'shareSubscribeEndDate'
    | 'payBeforeDate'
    | 'warrantSubscribeEndDate'
  >;
}) => {
  const { i18n } = useLingui();

  const { link } = useNavigate();

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Trans id="Summary" />
      </Typography>
      <Alert>
        <Trans id="Here is a summary. Before you proceed, you should verify that everything is correct" />
      </Alert>

      <Table sx={{ tableLayout: 'auto', maxWidth: 700 }}>
        <tbody>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Price per convertible before discount" />:
              </Typography>
            </td>
            <td>
              <NumberFormat
                locale={i18n.locale}
                variant="currency"
                currency={'SEK'}
                minimumFractionDigits={0}
                maximumFractionDigits={6}
              >
                {warrantProgram.pricePerWarrant}
              </NumberFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Discount" />:
              </Typography>
            </td>
            <td>
              <NumberFormat locale={i18n.locale} variant="percentage">
                {warrantProgram.illiquidityDiscount}
              </NumberFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Duration" />:
              </Typography>
            </td>
            <td>
              <Trans
                id="{duration} months"
                values={{
                  duration:
                    warrantProgram.warrantStartDate && warrantProgram.warrantEndDate
                      ? Math.round(
                          toDateTime(warrantProgram.warrantEndDate)?.diff(
                            toDateTime(warrantProgram.warrantStartDate),
                            'months',
                            {
                              conversionAccuracy: 'longterm',
                            },
                          ).months ?? 0,
                        )
                      : 0,
                }}
              />
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Strike-price per share" />:
              </Typography>
            </td>
            <td>
              <NumberFormat
                locale={i18n.locale}
                variant="currency"
                currency={'SEK'}
                minimumFractionDigits={2}
                maximumFractionDigits={6}
              >
                {warrantProgram.strikePrice}
              </NumberFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="The program starts" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.warrantStartDate}</DateFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Last day to subscribe for convertibles" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.warrantSubscribeEndDate}</DateFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Last day to pay for the convertibles" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.payBeforeDate}</DateFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="First day to subscribe for shares" />:
              </Typography>
            </td>
            <td>
              <Trans id="From when the options has been registered with Bolagsverket" />
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Last day to subscribe for shares" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.shareSubscribeEndDate}</DateFormat>
            </td>
          </tr>
        </tbody>
      </Table>

      <Box>
        <Button component={Link} to={link(wpEditOptionsSubsLink(warrantProgram))}>
          <Trans id="Continue" />
        </Button>
      </Box>
    </Stack>
  );
};
